<template>
  <div class="customer-selector-wrapper">
    <CustomerSelector 
      :model-value="internalCustomer"
      :order-type="orderType"
      :initial-selected-address-id="internalAddressId"
      @update:model-value="handleCustomerUpdate"
      @create-customer="$emit('create-customer')"
      @address-selected="handleAddressSelected"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import CustomerSelector from './CustomerSelector.vue';
import type { Customer, CustomerAddress } from '@/types/customer';

const props = defineProps<{
  modelValue: Customer | null;
  orderType: 'Delivery' | 'Takeaway';
  initialSelectedAddressId?: number | null;
}>();

const emit = defineEmits<{
  'update:modelValue': [customer: Customer | null];
  'create-customer': [];
  'address-selected': [address: CustomerAddress | null];
}>();

// Internal state to isolate from parent re-renders
const internalCustomer = ref<Customer | null>(null);
const internalAddressId = ref<number | null>(null);
const isInitialized = ref(false);

// Sync with parent props only when necessary
watch(() => props.modelValue, (newValue) => {
  if (newValue && (!internalCustomer.value || internalCustomer.value.id !== newValue.id)) {
    internalCustomer.value = { ...newValue };
  } else if (!newValue && internalCustomer.value) {
    internalCustomer.value = null;
  }
}, { immediate: true });

watch(() => props.initialSelectedAddressId, (newId) => {
  const normalizedId = newId ?? null;
  if (normalizedId !== internalAddressId.value) {
    internalAddressId.value = normalizedId;
  }
}, { immediate: true });

// Handle customer updates from CustomerSelector
function handleCustomerUpdate(customer: Customer | null) {
  internalCustomer.value = customer;
  emit('update:modelValue', customer);
}

// Handle address selection from CustomerSelector
function handleAddressSelected(address: CustomerAddress | null) {
  internalAddressId.value = address?.id || null;
  emit('address-selected', address);
}

// Mark as initialized after first sync
watch([internalCustomer, internalAddressId], () => {
  if (!isInitialized.value && (internalCustomer.value || internalAddressId.value !== null)) {
    isInitialized.value = true;
  }
}, { immediate: true });
</script>

<style scoped>
.customer-selector-wrapper {
  /* Ensure the wrapper doesn't affect layout */
  display: contents;
}
</style>
